import { SampleDatasetSelect } from "@humansignal/app-common/blocks/SampleDatasetSelect/SampleDatasetSelect";
import { ff, formatFileSize } from "@humansignal/core";
import {
  IconCode,
  IconCross,
  IconErrorAlt,
  IconFileUpload,
  IconInfoOutline,
  IconTrash,
  IconUpload,
  IconCheckCircleFilledGreen,
} from "@humansignal/icons";
import { cn as scn } from "@humansignal/shad/utils";
import { useAtomValue } from "jotai";
import Input from "libs/datamanager/src/components/Common/Input/Input";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useAPI } from "../../../providers/ApiProvider";
import { useTranslation } from "../../../i18n/LocaleProvider";
import { cn } from "../../../utils/bem";
import { unique } from "../../../utils/helpers";
import { sampleDatasetAtom } from "../utils/atoms";
import { requiredKeysFromConfig } from "../utils/labelConfig";
import "./Import.prefix.css";
import {
  Button,
  CodeBlock,
  SimpleCard,
  Spinner,
  Tooltip,
  Typography,
  Badge,
  useToast,
  ToastType,
} from "@humansignal/ui";
import truncate from "truncate-middle";
import samples from "./samples.json";
import { importFiles } from "./utils";
import * as extensions from "@afriannotate/extensions";

// Ways of bringing data in that an installed extension adds, as extra tabs.
const extensionSources = extensions.importSources ?? [];
import { MediaSplitter } from "../../../components/MediaSplitter/MediaSplitter";
import { HFImportPanel } from "../../../components/HFImportPanel/HFImportPanel";
import { Modal } from "../../../components/Modal/Modal";

/** Per-file CSV/TSV column-mapping modal. Opens right after a structured
 *  file uploads so people who don't know about headers or $variables can
 *  still land their columns on the fields the labeling setup expects:
 *  a "first row is column names" toggle (headerless files are common in
 *  the field) and one dropdown per column mapping it to a config
 *  variable, keeping its name, or skipping it. */
/** Display-shorten a column name / sample — the native <select> sizes
 *  itself to its longest option, so a sentence-length "column name"
 *  (classic no-header file) exploded the modal without this. */
const shortCol = (value, max = 42) => {
  const s = String(value);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

/** A "column name" that reads like a sentence means the file almost
 *  certainly has NO header row — its first data row got parsed as names. */
const looksHeaderless = (columns) => columns.some((c) => String(c).length > 60 || String(c).split(/\s+/).length > 8);

const CsvMappingModal = ({ fileName, columns, requiredKeys, initial, onApply, onCancel }) => {
  const [hasHeader, setHasHeader] = useState(initial?.has_header ?? !looksHeaderless(columns));
  const [map, setMap] = useState(() => {
    const m = {};
    columns.forEach((col, i) => {
      const preset = initial?.columns?.[col] ?? initial?.columns?.[String(i)];
      if (preset) {
        m[String(i)] = preset;
        return;
      }
      // Best-effort defaults: exact (case-insensitive) name match, or the
      // single obvious pairing when there's one column and one variable.
      const match = requiredKeys.find((k) => k.toLowerCase() === String(col).toLowerCase());
      m[String(i)] = match ?? (columns.length === 1 && requiredKeys.length === 1 ? requiredKeys[0] : "");
    });
    return m;
  });

  const apply = () => {
    const out = {};
    columns.forEach((col, i) => {
      const target = map[String(i)];
      if (target) out[hasHeader ? col : String(i)] = target;
    });
    onApply({ has_header: hasHeader, columns: out });
  };

  return (
    <Modal
      visible
      title={`Map columns. ${fileName}`}
      style={{ width: 560 }}
      onHide={onCancel}
      closeOnClickOutside={false}
    >
      <div className={importClass.elem("map").toClassName()}>
        <label className={importClass.elem("map-header").toClassName()}>
          <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
          <span>First row is column names</span>
        </label>
        <p className={importClass.elem("map-hint").toClassName()}>
          {requiredKeys.length
            ? `Point each column at a field your labeling setup uses (${requiredKeys.map((k) => `$${k}`).join(", ")}).`
            : "Choose what each column should be called in your tasks."}
        </p>
        <div className={importClass.elem("map-rows").toClassName()}>
          {columns.map((col, i) => (
            <div key={i} className={importClass.elem("map-row").toClassName()}>
              <span className={importClass.elem("map-col").toClassName()} title={String(col)}>
                {hasHeader ? shortCol(col) : `Column ${i + 1}`}
                {!hasHeader && (
                  <em className={importClass.elem("map-sample").toClassName()}> e.g. “{shortCol(col, 32)}”</em>
                )}
              </span>
              <select
                className={importClass.elem("map-select").toClassName()}
                value={map[String(i)] ?? ""}
                onChange={(e) => setMap((m) => ({ ...m, [String(i)]: e.target.value }))}
              >
                <option value="">{hasHeader ? `Keep name “${shortCol(col, 28)}”` : `Keep as “column_${i + 1}”`}</option>
                {requiredKeys.map((k) => (
                  <option key={k} value={k}>{`$${k}`}</option>
                ))}
                <option value="__skip__">Skip this column</option>
              </select>
            </div>
          ))}
        </div>
        <div className={importClass.elem("map-actions").toClassName()}>
          <Button look="outlined" onClick={onCancel} aria-label="Keep columns as they are">
            Keep as is
          </Button>
          <Button onClick={apply} aria-label="Apply column mapping">
            Apply mapping
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const importClass = cn("upload_page");
const dropzoneClass = cn("dropzone");

// Constants for file display and animation
const FLASH_ANIMATION_DURATION = 2000; // 2 seconds
const FILENAME_TRUNCATE_START = 24;
const FILENAME_TRUNCATE_END = 24;

function flatten(nested) {
  return [].concat(...nested);
}

// Keep in sync with core.settings.SUPPORTED_EXTENSIONS on the BE.
const supportedExtensions = {
  text: ["txt"],
  audio: ["wav", "mp3", "flac", "m4a", "ogg"],
  video: ["mp4", "webm"],
  // heic/heif/tif/tiff/avif are converted to JPEG or PNG by the server.
  image: ["bmp", "gif", "jpg", "jpeg", "png", "svg", "webp", "heic", "heif", "tif", "tiff", "avif"],
  html: ["html", "htm", "xml"],
  pdf: ["pdf"],
  structuredData: ["csv", "tsv", "json"],
};
const allSupportedExtensions = flatten(Object.values(supportedExtensions));

// Which object tag accepts which of the groups above. Mirrors
// TAG_EXTENSIONS in data_import/uploader.py, which refuses the same
// upload server-side; this copy only exists so the refusal arrives
// before the bytes go up rather than after.
const tagExtensions = {
  Image: supportedExtensions.image,
  Audio: supportedExtensions.audio,
  Video: supportedExtensions.video,
  VideoTiers: supportedExtensions.video,
  AudioTextAlign: supportedExtensions.audio,
  HyperText: supportedExtensions.html,
  Pdf: supportedExtensions.pdf,
};
const tagNouns = {
  Image: "images",
  Audio: "audio files",
  Video: "videos",
  VideoTiers: "videos",
  AudioTextAlign: "audio files",
  HyperText: "HTML files",
  Pdf: "PDFs",
};
// csv/tsv/json may be a list of tasks rather than one media object, so
// they are never judged against the tag.
const taskListExtensions = ["csv", "tsv", "json", "txt"];

/** The single object tag a config expects, or null when the config has
 *  none or more than one (multi-field projects import via CSV/JSON,
 *  where the mapping is explicit). */
const singleObjectTag = (labelConfig) => {
  const tags = Object.keys(tagExtensions).filter((tag) => new RegExp(`<${tag}\\b`, "i").test(labelConfig || ""));
  return tags.length === 1 ? tags[0] : null;
};

function getFileExtension(fileName) {
  if (!fileName) {
    return fileName;
  }
  return fileName.split(".").pop().toLowerCase();
}

function traverseFileTree(item, path) {
  return new Promise((resolve) => {
    path = path || "";
    if (item.isFile) {
      // Avoid hidden files
      if (item.name[0] === ".") return resolve([]);

      resolve([item]);
    } else if (item.isDirectory) {
      // Get folder contents
      const dirReader = item.createReader();
      const dirPath = `${path + item.name}/`;

      // A folder is read in batches (100 entries at a time in Chrome);
      // one call alone silently drops the rest of a large folder.
      const all = [];
      const readBatch = () =>
        dirReader.readEntries(
          (entries) => {
            if (entries.length) {
              all.push(...entries);
              readBatch();
              return;
            }
            Promise.all(all.map((entry) => traverseFileTree(entry, dirPath)))
              .then(flatten)
              .then(resolve);
          },
          () => resolve([]),
        );
      readBatch();
    }
  });
}

function getFiles(files) {
  // @todo this can be not a files, but text or any other draggable stuff
  return new Promise((resolve) => {
    if (!files.length) return resolve([]);
    if (!files[0].webkitGetAsEntry) return resolve(files);

    // Use DataTransferItemList interface to access the file(s)
    const entries = Array.from(files).map((file) => file.webkitGetAsEntry());

    Promise.all(entries.map(traverseFileTree))
      .then(flatten)
      .then((fileEntries) => fileEntries.map((fileEntry) => new Promise((res) => fileEntry.file(res))))
      .then((filePromises) => Promise.all(filePromises))
      .then(resolve);
  });
}

const Upload = ({ children, sendFiles }) => {
  const [hovered, setHovered] = useState(false);
  const onHover = (e) => {
    e.preventDefault();
    setHovered(true);
  };
  const onLeave = setHovered.bind(null, false);
  const dropzoneRef = useRef();

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      onLeave();
      getFiles(e.dataTransfer.items).then((files) => sendFiles(files));
    },
    [onLeave, sendFiles],
  );

  return (
    <div
      id="holder"
      className={dropzoneClass.mod({ hovered }).toClassName()}
      ref={dropzoneRef}
      onDragStart={onHover}
      onDragOver={onHover}
      onDragLeave={onLeave}
      onDrop={onDrop}
      // {...getRootProps}
    >
      {children}
    </div>
  );
};

const ErrorMessage = ({ error, onDismiss }) => {
  if (!error) return null;
  let extra = error.validation_errors ?? error.extra;
  // support all possible responses

  if (extra && typeof extra === "object" && !Array.isArray(extra)) {
    extra = extra.non_field_errors ?? Object.values(extra);
  }
  if (Array.isArray(extra)) extra = extra.join("; ");

  return (
    <div className={importClass.elem("error").toClassName()}>
      <IconErrorAlt width="24" height="24" />
      {error.id && `[${error.id}] `}
      <span className="flex-1">
        {error.detail || error.message}
        {extra && ` (${extra})`}
      </span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss error" className="errorDismiss">
          <IconCross width="16" height="16" />
        </button>
      )}
    </div>
  );
};

export const ImportPage = ({
  project,
  sample,
  show = true,
  onWaiting,
  onFileListUpdate,
  onSampleDatasetSelect,
  highlightCsvHandling,
  dontCommitToProject = false,
  csvHandling,
  setCsvHandling,
  addColumns,
  uploadedColumns,
  openLabelingConfig,
  goldMode = false,
  setGoldMode,
  answerColumn = "answer",
  setAnswerColumn,
  csvMappings = {},
  setCsvMappings,
  initialSource,
  sourceState,
  setSourceState,
  // Ids of the data-source tabs to offer; all of them when not given.
  sources,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState();
  // Data-source tab: which import mechanism the user is using.
  // 'files' — drag/drop + Browse Files (the traditional flow).
  // 'urls'  — paste one or more URLs into the URL form.
  // 'hf'    — pull a Hugging Face Hub dataset via the HFImportPanel.
  // The wizard's Upload dropzone + URL form both feed importFiles()
  // under the hood; the tab split is a UX affordance only.
  const [dataSourceTab, setDataSourceTab] = useState(initialSource ?? "files");
  // Files waiting for the column-mapping modal (shown one at a time).
  const [mappingQueue, setMappingQueue] = useState([]);
  const [newlyUploadedFiles, setNewlyUploadedFiles] = useState(new Set());
  const prevUploadedRef = useRef(new Set());
  const api = useAPI();
  const toast = useToast();
  const projectConfigured = project?.label_config !== "<View></View>";
  const sampleConfig = useAtomValue(sampleDatasetAtom);

  // Required $varname keys per the label config (e.g. ["text", "audio"]).
  // Empty when the config is the default ``<View></View>``.
  const requiredKeysList = useMemo(() => requiredKeysFromConfig(project?.label_config), [project?.label_config]);
  // The object tag media must match, when the config names exactly one.
  const expectedTag = useMemo(() => singleObjectTag(project?.label_config), [project?.label_config]);
  // CSV-handling-pending masks the column list to a single placeholder.
  // Comparing during that window would lie, so we skip the check.
  const csvPlaceholderActive = ["choose", "ts"].includes(csvHandling);
  // Per-file column tracking. The reimport endpoint returns one set
  // of ``data_columns`` per BATCH of file_upload_ids. We attribute
  // those columns to each id in the batch — gives us file-level
  // granularity for the warning even though the API is batch-level.
  // ``Map<fileUploadId: number, columns: string[]>``.
  const [columnsByFileId, setColumnsByFileId] = useState({});
  // Columns this file will ACTUALLY produce, with the user's column
  // mapping applied — renames, skips, and headerless ``column_N``
  // fallbacks. Without this, mapping a column to $text still tripped the
  // "your data doesn't include $text" warning (it compared raw columns).
  const effectiveColumnsFor = useCallback(
    (fileId) => {
      const cols = columnsByFileId[fileId];
      if (!cols) return cols;
      const mapping = csvMappings?.[fileId];
      if (!mapping) return cols;
      const headerless = mapping.has_header === false;
      const out = [];
      cols.forEach((col, i) => {
        const key = headerless ? String(i) : col;
        const target = mapping.columns?.[key];
        if (target === "__skip__") return;
        out.push(target || (headerless ? `column_${i + 1}` : col));
      });
      return out;
    },
    [columnsByFileId, csvMappings],
  );

  const missingKeysFor = useCallback(
    (fileId) => {
      if (csvPlaceholderActive) return [];
      const cols = effectiveColumnsFor(fileId);
      // No columns recorded for this file yet (fresh upload, server
      // didn't report data_columns for the batch). Treat as no missing
      // keys; LSF will validate at task creation if there's a real
      // mismatch.
      if (!cols) return [];
      // A dropped media file has no column names, so the server reports
      // the single column ``$undefined$`` and binds the file to the
      // config's one object tag when the task is built. Comparing that
      // sentinel against the config's $video would report every video
      // upload as missing its own field. The backend discards the same
      // sentinel before its equivalent check (Project._validate_there_
      // are_data_columns_in_config).
      if (cols.includes("$undefined$") && requiredKeysList.length <= 1) return [];
      return requiredKeysList.filter((k) => !cols.includes(k));
    },
    [csvPlaceholderActive, effectiveColumnsFor, requiredKeysList],
  );
  // ``missingKeysAggregate`` is computed below the ``files`` reducer
  // because it reads ``files.uploaded``. See after the dispatch
  // setup further down in this component.

  const processFiles = (state, action) => {
    if (action.sending) {
      return { ...state, uploading: [...action.sending, ...state.uploading] };
    }
    if (action.sent) {
      return {
        ...state,
        uploading: state.uploading.filter((f) => !action.sent.includes(f)),
      };
    }
    if (action.failed) {
      // One failed file moves to its own list; the others keep going.
      return {
        ...state,
        uploading: state.uploading.filter((f) => f !== action.failed),
        failed: [
          ...state.failed.filter((x) => x.file !== action.failed),
          { file: action.failed, message: action.message },
        ],
      };
    }
    if (action.dismissFailed) {
      return { ...state, failed: state.failed.filter((x) => x.file !== action.dismissFailed) };
    }
    if (action.uploaded) {
      return {
        ...state,
        uploaded: unique([...state.uploaded, ...action.uploaded], (a, b) => a.id === b.id),
      };
    }
    if (action.ids) {
      const ids = unique([...state.ids, ...action.ids]);

      onFileListUpdate?.(ids);
      return { ...state, ids };
    }
    if (action.removeId !== undefined) {
      const ids = state.ids.filter((i) => i !== action.removeId);
      onFileListUpdate?.(ids);
      return {
        ...state,
        uploaded: state.uploaded.filter((f) => f.id !== action.removeId),
        ids,
      };
    }
    return state;
  };

  const [files, dispatch] = useReducer(processFiles, {
    uploaded: [],
    uploading: [],
    failed: [],
    ids: [],
  });
  // Audio / video splitter: opened from an uploaded recording's card.
  const [splitting, setSplitting] = useState(null); // uploaded file row
  // Per-file bytes sent so far, keyed by the File object, so every card
  // shows its own movement instead of one idle spinner for the lot.
  const [progress, setProgress] = useState(() => new Map());
  const setFileProgress = useCallback((file, fraction, loaded) => {
    setProgress((prev) => {
      const next = new Map(prev);
      next.set(file, { fraction, loaded });
      return next;
    });
  }, []);
  const showList = Boolean(files.uploaded?.length || files.uploading?.length || files.failed?.length || sample);

  // Aggregate of all missing required keys across every uploaded
  // file. Used to drive the page-level warning banner; per-file
  // detail comes from missingKeysFor(file.id) inside the file row.
  const missingKeys = useMemo(
    () => requiredKeysList.filter((k) => files.uploaded.some((f) => (missingKeysFor(f.id) || []).includes(k))),
    [files.uploaded, missingKeysFor, requiredKeysList],
  );

  const loadFilesList = useCallback(
    async (file_upload_ids) => {
      const query = {};

      if (file_upload_ids) {
        // should be stringified array "[1,2]"
        query.ids = JSON.stringify(file_upload_ids);
      }
      const files = await api.callApi("fileUploads", {
        params: { pk: project.id, ...query },
      });

      dispatch({ uploaded: files ?? [] });

      if (files?.length) {
        dispatch({ ids: files.map((f) => f.id) });
      }
      return files;
    },
    [project?.id],
  );

  const onError = (err) => {
    console.error(err);
    // @todo workaround for error about input size in a wrong html format
    if (typeof err === "string" && err.includes("RequestDataTooBig")) {
      const message = t("misc.imported_file_is_too_big");
      const extra = err.match(/"exception_value">(.*)<\/pre>/)?.[1];

      err = { message, extra };
    }
    setError(err);
    onWaiting?.(false);
  };
  const removeUploadedFile = useCallback(
    async (file) => {
      if (!project?.id || !file?.id) return;
      const res = await api.callApi("deleteFileUploads", {
        params: { pk: project.id },
        body: { file_upload_ids: [file.id] },
      });
      if (res && !res.error) {
        dispatch({ removeId: file.id });
        setColumnsByFileId((prev) => {
          if (!(file.id in prev)) return prev;
          const next = { ...prev };
          delete next[file.id];
          return next;
        });
      } else {
        // Surface the error in the page-level banner; the user can
        // retry. Non-blocking — file row stays visible so they see
        // it didn't go.
        setError(res?.error ?? new Error("Failed to remove file"));
      }
    },
    [project?.id, api],
  );

  // Structured file (CSV/TSV) handling — infer the right mode from the
  // project's labeling config instead of blocking the wizard on a jargon
  // radio ("List of tasks" vs "Time Series") most importers didn't
  // understand: the wrong pick produced one task whose $text was the FILE
  // URL. TimeSeries / valueType="url" configs read the whole file; every
  // other config wants one task per row. The radio stays visible so the
  // rare exception can flip it.
  const inferCsvHandling = useCallback(() => {
    const cfg = project?.label_config || "";
    if (/<TimeSeries\b/i.test(cfg) || /valueType="url"/i.test(cfg)) return "ts";
    return "tasks";
  }, [project?.label_config]);

  const onFinish = useCallback(
    async (res) => {
      const { could_be_tasks_list, data_columns, file_upload_ids } = res;

      dispatch({ ids: file_upload_ids });
      if (could_be_tasks_list && !csvHandling) setCsvHandling(inferCsvHandling());
      // Offer the column-mapping modal for freshly uploaded structured
      // files (rows-as-tasks mode, real columns parsed). ``$undefined$``
      // marks column-less text files — nothing to map there.
      const handling = csvHandling || inferCsvHandling();
      const mappableColumns = (data_columns || []).filter((c) => c !== "$undefined$");
      // Files whose columns already carry every required $variable (and
      // don't look headerless) import silently — the modal only appears
      // when there's an actual decision to make. The file card's "Map
      // columns" button still allows manual changes either way.
      const coversRequired = requiredKeysList.length > 0 && requiredKeysList.every((k) => mappableColumns.includes(k));
      const needsMappingModal = looksHeaderless(mappableColumns) || (requiredKeysList.length > 0 && !coversRequired);
      if (
        could_be_tasks_list &&
        handling === "tasks" &&
        mappableColumns.length &&
        needsMappingModal &&
        Array.isArray(file_upload_ids)
      ) {
        setMappingQueue((q) => [
          ...q,
          ...file_upload_ids
            .filter((id) => !q.some((entry) => entry.fileId === id))
            .map((id) => ({ fileId: id, columns: mappableColumns })),
        ]);
      }
      onWaiting?.(false);
      addColumns(data_columns);
      // Attribute the batch's data_columns to each file id in the
      // batch so per-file mismatch detection has the data it needs.
      if (Array.isArray(file_upload_ids) && Array.isArray(data_columns)) {
        setColumnsByFileId((prev) => {
          const next = { ...prev };
          for (const id of file_upload_ids) {
            next[id] = data_columns;
          }
          return next;
        });
      }

      await loadFilesList(file_upload_ids);
      return res;
    },
    [addColumns, loadFilesList, inferCsvHandling, requiredKeysList],
  );

  // Track newly uploaded files for flash animation
  useEffect(() => {
    const currentUploadedIds = new Set(files.uploaded.map((f) => f.id));
    const previousUploadedIds = prevUploadedRef.current;

    // Find files that were just uploaded (in current but not in previous)
    const justUploaded = new Set([...currentUploadedIds].filter((id) => !previousUploadedIds.has(id)));

    // Update the ref immediately after comparison to ensure it's available for next run
    prevUploadedRef.current = new Set(currentUploadedIds);

    // Clean up animation state for files that are no longer in the uploaded list
    setNewlyUploadedFiles((prev) => {
      const filtered = new Set([...prev].filter((id) => currentUploadedIds.has(id)));
      return filtered;
    });

    // Animate newly uploaded files (including first upload)
    if (justUploaded.size > 0) {
      // Apply animation class immediately for better responsiveness
      setNewlyUploadedFiles((prev) => new Set([...prev, ...justUploaded]));

      // Remove animation class after animation completes (CSS handles the animation timing)
      const timeoutId = setTimeout(() => {
        setNewlyUploadedFiles((prev) => {
          const updated = new Set(prev);
          justUploaded.forEach((id) => updated.delete(id));
          return updated;
        });
      }, FLASH_ANIMATION_DURATION);

      // Cleanup timeout on unmount or dependency change
      return () => clearTimeout(timeoutId);
    }
  }, [files.uploaded]);

  const importFilesImmediately = useCallback(
    async (files, body) => {
      importFiles({
        files,
        body,
        project,
        onError,
        onFinish,
        onFileProgress: setFileProgress,
        onFileDone: (file) => dispatch({ sent: [file] }),
        onFileError: (file, message) => dispatch({ failed: file, message }),
        onUploadStart: (files) => dispatch({ sending: files }),
        onUploadFinish: (files) => {
          dispatch({ sent: files });
          setProgress((prev) => {
            const next = new Map(prev);
            for (const f of files) next.delete(f);
            return next;
          });
        },
        dontCommitToProject,
      });
    },
    [project, onFinish, setFileProgress],
  );

  /** A file refused before it was ever sent. Nothing was added to the
   *  file list, so there is no row to remove and no later upload to
   *  clear a banner — it would sit there until the page was left. A
   *  toast says the same thing and takes itself away. */
  const rejectFile = useCallback(
    (message) => {
      onWaiting?.(false);
      if (toast) toast.show({ message, type: ToastType.error });
      else onError(new Error(message));
    },
    [toast, onWaiting],
  );

  const sendFiles = useCallback(
    async (files) => {
      setError(null);
      onWaiting?.(true);
      files = [...files]; // they can be array-like object
      const fd = new FormData();

      // Files can be dropped before the project has loaded (the modal
      // opens on ``projects/undefined/import`` for a moment). Nothing
      // useful can be sent yet; say so instead of a 404 per file.
      if (!project?.id) {
        rejectFile("The project is still loading. Drop the files again in a moment.");
        return;
      }

      for (const f of files) {
        const ext = getFileExtension(f.name);

        if (!allSupportedExtensions.includes(ext)) {
          rejectFile(`The filetype of file "${f.name}" is not supported.`);
          return;
        }
        // Supported by the platform is not the same as usable by THIS
        // project: .mp3 files dropped into an <Image> project used to
        // upload happily and only fail when an annotator opened one.
        if (expectedTag && !taskListExtensions.includes(ext) && !tagExtensions[expectedTag].includes(ext)) {
          rejectFile(
            `This project expects ${tagNouns[expectedTag]}, but "${f.name}" is not one. ` +
              `Accepted here: ${tagExtensions[expectedTag].join(", ")}.`,
          );
          return;
        }
        fd.append(f.name, f);
      }

      // Refuse a batch the org's storage quota cannot hold BEFORE any
      // bytes travel: the server would accept the first files and
      // reject the rest, leaving a half-imported drop. Best effort — if
      // the limits call fails the server-side check still applies.
      try {
        const limits = await api.callApi("importLimits", { params: { pk: project.id }, errorFilter: () => true });
        const remaining = limits?.storage_remaining_bytes;
        if (typeof remaining === "number") {
          let total = 0;
          for (const f of files) total += f.size || 0;
          if (total > remaining) {
            rejectFile(
              `These files add up to ${formatFileSize(total)} but this project has only ` +
                `${formatFileSize(remaining)} of its storage allowance left. Remove data you no longer need, ` +
                "connect cloud storage instead, or ask your organisation owner to request a larger allowance.",
            );
            return;
          }
        }
      } catch {
        /* server still enforces the quota */
      }
      return importFilesImmediately(files, fd);
    },
    [api, project?.id, importFilesImmediately, expectedTag, rejectFile],
  );

  const onUpload = useCallback(
    (e) => {
      sendFiles(e.target.files);
      e.target.value = "";
    },
    [sendFiles],
  );

  const onLoadURL = useCallback(
    (e) => {
      e.preventDefault();
      setError(null);
      const url = urlRef.current?.value;

      if (!url) {
        return;
      }
      urlRef.current.value = "";
      onWaiting?.(true);
      const body = new URLSearchParams({ url });

      importFilesImmediately([{ name: url }], body);
    },
    [importFilesImmediately],
  );

  // Paste tab — accept a JSON task array, a single task object, or JSONL
  // (one JSON object per line). The pasted text is normalized into an
  // in-memory .json File and fed through the SAME sendFiles() path a
  // dropped file takes, so server-side parsing, the file list, and the
  // required-fields check behave identically.
  const onImportPasted = useCallback(
    (e) => {
      e.preventDefault();
      setError(null);
      const raw = (pasteRef.current?.value || "").trim();
      if (!raw) return;
      let tasks = null;
      try {
        const parsed = JSON.parse(raw);
        tasks = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        // Not a single JSON document — try JSONL before giving up.
        try {
          const lines = raw
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter(Boolean);
          if (!lines.length) throw new Error("empty");
          tasks = lines.map((l) => JSON.parse(l));
        } catch {
          onError(
            new Error(
              "Couldn't parse the pasted text. Paste a JSON array of tasks, a single task object, or JSONL (one JSON object per line).",
            ),
          );
          return;
        }
      }
      if (!tasks.length) return;
      const file = new File([JSON.stringify(tasks)], `pasted-tasks-${Date.now()}.json`, {
        type: "application/json",
      });
      pasteRef.current.value = "";
      sendFiles([file]);
    },
    [sendFiles, onError],
  );

  const openConfig = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      openLabelingConfig?.();
    },
    [openLabelingConfig],
  );

  useEffect(() => {
    if (project?.id !== undefined) {
      loadFilesList().then((files) => {
        if (csvHandling) return;
        // empirical guess on start if we have some possible tasks list/structured data problem
        if (Array.isArray(files) && files.some(({ file }) => /\.[ct]sv$/.test(file))) {
          setCsvHandling(inferCsvHandling());
        }
      });
    }
  }, [project?.id, loadFilesList]);

  const urlRef = useRef();
  const pasteRef = useRef();

  if (!project) return null;
  if (!show) return null;

  const csvProps = {
    name: "csv",
    type: "radio",
    onChange: (e) => setCsvHandling(e.target.value),
  };

  const dataSourceTabs = [
    { id: "files", label: t("pages.createproject.import.tab_files", undefined, "Upload files") },
    { id: "urls", label: t("pages.createproject.import.tab_urls", undefined, "URLs") },
    { id: "paste", label: t("pages.createproject.import.tab_paste", undefined, "Paste") },
    { id: "hf", label: t("pages.createproject.import.tab_hf", undefined, "Hugging Face Hub") },
    ...extensionSources.map((s) => ({ id: s.id, label: s.label })),
  ].filter((tab) => !sources || sources.includes(tab.id));
  const ExtensionSource = extensionSources.find((s) => s.id === dataSourceTab)?.component;
  // Tabs that draw their whole panel themselves (no file list, no drop box).
  const ownPanel = dataSourceTab === "hf" || !!ExtensionSource;

  return (
    <div className={importClass}>
      {highlightCsvHandling && <div className={importClass.elem("csv-splash").toClassName()} />}
      <input
        id="file-input"
        type="file"
        name="file"
        multiple
        accept=".csv,.tsv,.json,.jsonl,.txt,.parquet,.html,.htm,.xml,image/*,audio/*,video/*,.pdf"
        onChange={onUpload}
        style={{ display: "none" }}
      />

      {/* Data-source tab switcher. Sits above the source-specific
          inputs so the user picks how they want to bring data in
          before touching any control. Sample datasets pill stays in
          the header on the Files/URLs tabs (irrelevant for HF). */}
      <div className={importClass.elem("tabs").toClassName()} role="tablist" aria-label="Import source">
        {dataSourceTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={dataSourceTab === t.id}
            className={importClass
              .elem("tab-btn")
              .mod({ active: dataSourceTab === t.id })
              .toClassName()}
            onClick={() => setDataSourceTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!ownPanel && (
        <header className="flex gap-4">
          {dataSourceTab === "urls" && (
            <form
              className={`${importClass.elem("url-form")} inline-flex items-stretch`}
              method="POST"
              onSubmit={onLoadURL}
            >
              <Input placeholder={t("misc.dataset_url")} name="url" ref={urlRef} rawClassName="h-[40px]" />
              <Button variant="primary" look="outlined" type="submit" aria-label={t("misc.add_url")}>
                {t("misc.add_url")}
              </Button>
            </form>
          )}
          {dataSourceTab === "files" && (
            <Button
              variant="primary"
              look="outlined"
              type="button"
              onClick={() => document.getElementById("file-input").click()}
              leading={<IconUpload />}
              aria-label={t("misc.upload_file")}
            >
              Upload {files.uploaded.length ? "More " : ""}Files
            </Button>
          )}
          {ff.isActive(ff.FF_SAMPLE_DATASETS) && !ownPanel && (
            <SampleDatasetSelect samples={samples} sample={sample} onSampleApplied={onSampleDatasetSelect} />
          )}
          <div
            className={importClass
              .elem("csv-handling")
              .mod({ highlighted: highlightCsvHandling, hidden: !csvHandling })
              .toClassName()}
          >
            <span>{t("misc.treat_csvtsv_as", undefined, "CSV/TSV:")}</span>
            <label>
              <input {...csvProps} value="tasks" checked={csvHandling === "tasks"} />{" "}
              {t("pages.createproject.import.csv_rows", undefined, "Each row is a task")}
            </label>
            <label>
              <input {...csvProps} value="ts" checked={csvHandling === "ts"} />{" "}
              {t("pages.createproject.import.csv_whole", undefined, "Whole file is one task (time series / document)")}
            </label>
          </div>
          <div className={importClass.elem("status").toClassName()}>
            {files.uploaded.length ? `${files.uploaded.length} files uploaded` : ""}
          </div>
        </header>
      )}

      <ErrorMessage error={error} onDismiss={() => setError(null)} />

      {/* Gold / honeypot answers affordance. When on, the committed tasks
          are seeded with a ground-truth answer that the real-time scorer
          measures annotators against — honeypots stay blind in the
          annotator UI. Each row = task data + an answer column (Choices),
          or full LS task-with-annotations JSON for advanced configs. */}
      {setGoldMode && !ownPanel && (
        <div className={importClass.elem("gold").toClassName()}>
          <label className={importClass.elem("gold-toggle").toClassName()}>
            <input type="checkbox" checked={!!goldMode} onChange={(e) => setGoldMode(e.target.checked)} />
            <span>{t("pages.createproject.import.gold_label", undefined, "These are gold / honeypot answers")}</span>
          </label>
          <p className={importClass.elem("gold-hint").toClassName()}>
            {t(
              "pages.createproject.import.gold_hint",
              undefined,
              "Each imported task is seeded with a correct answer and used to score annotators. Honeypots stay invisible to annotators.",
            )}
          </p>
          {goldMode && (
            <div className={importClass.elem("gold-answer").toClassName()}>
              <label htmlFor="gold-answer-column">
                {t("pages.createproject.import.gold_answer_column", undefined, "Answer column (Choices)")}
              </label>
              {uploadedColumns?.length ? (
                <select
                  id="gold-answer-column"
                  value={answerColumn}
                  onChange={(e) => setAnswerColumn?.(e.target.value)}
                >
                  {uploadedColumns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id="gold-answer-column"
                  value={answerColumn}
                  onChange={(e) => setAnswerColumn?.(e.target.value)}
                  placeholder="answer"
                  rawClassName="h-[36px] max-w-[240px]"
                />
              )}
            </div>
          )}
        </div>
      )}

      {missingKeys.length > 0 && (
        <div
          className={importClass.elem("error").toClassName()}
          role="alert"
          data-testid="import-required-fields-warning"
        >
          <IconErrorAlt width="24" height="24" />
          <span>
            Your label config expects {missingKeys.length === 1 ? "field" : "fields"}{" "}
            {missingKeys.map((k) => `$${k}`).join(", ")} but the uploaded data doesn’t include{" "}
            {missingKeys.length === 1 ? "it" : "them"}. Either edit the data so it has{" "}
            {missingKeys.length === 1 ? "that key" : "those keys"} or{" "}
            <Button
              type="button"
              look="string"
              onClick={(e) => {
                e.preventDefault();
                openLabelingConfig?.();
              }}
              className="border-none bg-none p-0 m-0 text-primary-content underline"
            >
              adjust the labeling config
            </Button>
            .
          </span>
        </div>
      )}

      {ExtensionSource && (
        <div className={importClass.elem("hf-wrap").toClassName()}>
          <ExtensionSource
            project={project}
            requiredKeys={requiredKeysList}
            labelConfig={project?.label_config}
            // In the wizard nothing is written until Save: the source says
            // what to bring in, and its ``finish`` does it then.
            deferred={dontCommitToProject}
            state={sourceState?.[dataSourceTab]}
            setState={setSourceState ? (state) => setSourceState(dataSourceTab, state) : undefined}
          />
        </div>
      )}

      {dataSourceTab === "hf" && (
        <div className={importClass.elem("hf-wrap").toClassName()}>
          <HFImportPanel
            projectId={project?.id}
            requiredKeys={requiredKeysList}
            labelConfig={project?.label_config}
            // Intentionally NOT calling onFileListUpdate here — that
            // callback threads a list of integer file_upload primary
            // keys, and HF import writes Tasks directly to the DB
            // without going through the file_upload pipeline. Passing
            // a synthetic string id here would crash the wizard's
            // finishUpload / reimport paths (Field 'id' expected a
            // number). The wizard's Save action still works fine
            // because HF tasks are already persisted by the time the
            // user clicks Save.
          />
        </div>
      )}

      {/* Files/URLs main area. The Upload wrapper captures drag+drop
          on the region — we ONLY want that on the Files tab, so URLs
          tab renders the same content in a plain <div> and drops
          silently ignore. HF tab hides main entirely (its own panel
          renders above). */}
      {!ownPanel &&
        (dataSourceTab === "files" ? (
          <main>
            <Upload sendFiles={sendFiles} project={project}>
              <div
                className={scn("flex gap-4 w-full min-h-full", {
                  "justify-center": !showList,
                })}
              >
                {!showList && dataSourceTab === "files" && (
                  <div className="flex gap-4 justify-center items-start w-full h-full">
                    <label htmlFor="file-input" className="w-full h-full">
                      <div className={`${dropzoneClass.elem("content")} w-full`}>
                        <IconFileUpload height="64" className={dropzoneClass.elem("icon").toClassName()} />
                        <header>
                          Drag & drop files here
                          <br />
                          or click to browse
                        </header>

                        <p className={dropzoneClass.elem("formats").toClassName()}>
                          Images, audio, video, PDF, HTML, text and CSV, TSV or JSON.{" "}
                          <Tooltip
                            title={[
                              `Images: ${supportedExtensions.image.join(", ")}`,
                              `Audio: ${supportedExtensions.audio.join(", ")}`,
                              `Video: ${supportedExtensions.video.join(", ")} (support depends on your browser)`,
                              `HTML: ${supportedExtensions.html.join(", ")}`,
                              `Text: ${supportedExtensions.text.join(", ")}`,
                              `Structured data: ${supportedExtensions.structuredData.join(", ")}`,
                              `PDF: ${supportedExtensions.pdf.join(", ")}`,
                            ].join("\n")}
                          >
                            <a
                              href="https://afriannotate.waraka.org/docs/data-import/uploading-your-data"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              All formats
                            </a>
                          </Tooltip>
                        </p>
                        <p className={dropzoneClass.elem("tip").toClassName()}>
                          Media files work when your labeling setup has one field; with more fields, upload a JSON or
                          CSV that names each one. Large collections belong in{" "}
                          <a
                            href="https://afriannotate.waraka.org/docs/data-import/cloud-storage"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            connected cloud storage
                          </a>
                          .
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                {showList && (
                  <div className="w-full">
                    <SimpleCard
                      title={t("misc.files")}
                      className="w-full h-full"
                      contentClassName="overflow-y-auto h-[calc(100%-48px)]"
                    >
                      <div className={importClass.elem("file-cards").toClassName()}>
                        {sample && (
                          <div className={importClass.elem("file-card").mod({ sample: true }).toClassName()}>
                            <div className={importClass.elem("file-card-head").toClassName()}>
                              <Tooltip title={sample.title}>
                                <span className={importClass.elem("file-card-name").toClassName()}>{sample.title}</span>
                              </Tooltip>
                              <Badge>{t("misc.sample")}</Badge>
                            </div>
                            <div className={importClass.elem("file-card-meta").toClassName()}>{sample.description}</div>
                            <div className={importClass.elem("file-card-foot").toClassName()}>
                              <Button
                                size="smaller"
                                variant="negative"
                                look="outlined"
                                onClick={() => onSampleDatasetSelect(undefined)}
                              >
                                <IconTrash className="w-4 h-4" /> Remove
                              </Button>
                            </div>
                          </div>
                        )}
                        {files.uploaded.map((file) => {
                          const truncatedFilename = truncate(
                            file.file,
                            FILENAME_TRUNCATE_START,
                            FILENAME_TRUNCATE_END,
                            "...",
                          );
                          const missing = missingKeysFor(file.id);
                          const hasRequiredCheck =
                            !csvPlaceholderActive &&
                            requiredKeysList.length > 0 &&
                            (columnsByFileId[file.id] || []).length > 0;
                          const isOk = hasRequiredCheck && missing.length === 0;
                          const isMissing = hasRequiredCheck && missing.length > 0;
                          return (
                            <div
                              key={file.id ?? file.file}
                              className={importClass
                                .elem("file-card")
                                .mod({
                                  ok: isOk,
                                  missing: isMissing,
                                  flash: newlyUploadedFiles.has(file.id),
                                })
                                .toClassName()}
                            >
                              <button
                                type="button"
                                className={importClass.elem("file-card-remove").toClassName()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeUploadedFile(file);
                                }}
                                aria-label={`Remove ${file.file}`}
                                title="Remove file"
                              >
                                ✕
                              </button>
                              <div className={importClass.elem("file-card-head").toClassName()}>
                                <Tooltip title={file.file}>
                                  <span className={importClass.elem("file-card-name").toClassName()}>
                                    {truncatedFilename}
                                  </span>
                                </Tooltip>
                                {isOk && (
                                  <span
                                    className={importClass.elem("file-card-tick").toClassName()}
                                    aria-label="All required fields present"
                                  >
                                    <IconCheckCircleFilledGreen width="18" height="18" />
                                  </span>
                                )}
                                {isMissing && (
                                  <span
                                    className={importClass.elem("file-card-warn").toClassName()}
                                    aria-label="Missing required fields"
                                  >
                                    <IconErrorAlt width="18" height="18" />
                                  </span>
                                )}
                              </div>
                              <div className={importClass.elem("file-card-meta").toClassName()}>
                                {file.size ? formatFileSize(file.size) : ""}
                              </div>
                              {isOk && (
                                <div className={importClass.elem("file-card-chip").mod({ ok: true }).toClassName()}>
                                  All required fields present
                                </div>
                              )}
                              {isMissing && (
                                <div
                                  className={importClass.elem("file-card-chip").mod({ missing: true }).toClassName()}
                                >
                                  Missing {missing.length === 1 ? "field" : "fields"}:{" "}
                                  {missing.map((k) => "$" + k).join(", ")}
                                </div>
                              )}
                              {/\.(wav|mp3|flac|ogg|oga|m4a|aac|opus|aiff|aif|mp4|webm|mov|mkv|avi|m4v|mpeg|mpg|ogv)$/i.test(
                                file.file || "",
                              ) && (
                                <div className={importClass.elem("file-card-foot").toClassName()}>
                                  <Button
                                    size="smaller"
                                    look="outlined"
                                    onClick={() => setSplitting(file)}
                                    aria-label={`Split ${file.file} into clips`}
                                    disabled={!project?.id}
                                  >
                                    Split into clips
                                  </Button>
                                </div>
                              )}
                              {/\.[ct]sv$/.test(file.file || "") && (columnsByFileId[file.id] || []).length > 0 && (
                                <div className={importClass.elem("file-card-foot").toClassName()}>
                                  <Button
                                    size="smaller"
                                    look="outlined"
                                    onClick={() =>
                                      setMappingQueue((q) =>
                                        q.some((entry) => entry.fileId === file.id)
                                          ? q
                                          : [...q, { fileId: file.id, columns: columnsByFileId[file.id] }],
                                      )
                                    }
                                    aria-label={`Map columns for ${file.file}`}
                                  >
                                    {csvMappings?.[file.id] ? "Edit column mapping" : "Map columns"}
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {files.uploading.map((file, idx) => {
                          const truncatedFilename = truncate(
                            file.name,
                            FILENAME_TRUNCATE_START,
                            FILENAME_TRUNCATE_END,
                            "...",
                          );
                          const p = progress.get(file);
                          const pct = p ? Math.round(p.fraction * 100) : null;
                          return (
                            <div
                              key={`${idx}-${file.name}`}
                              className={importClass.elem("file-card").mod({ uploading: true }).toClassName()}
                            >
                              <div className={importClass.elem("file-card-head").toClassName()}>
                                <Tooltip title={file.name}>
                                  <span className={importClass.elem("file-card-name").toClassName()}>
                                    {truncatedFilename}
                                  </span>
                                </Tooltip>
                                <span
                                  className={importClass.elem("file-status").mod({ uploading: true }).toClassName()}
                                  role="progressbar"
                                  aria-label={`Uploading ${file.name}`}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                  aria-valuenow={pct ?? undefined}
                                >
                                  {pct !== null && (
                                    <span
                                      className={importClass.elem("file-progress-fill").toClassName()}
                                      style={{ width: `${pct}%` }}
                                    />
                                  )}
                                </span>
                              </div>
                              <div className={importClass.elem("file-card-meta").toClassName()}>
                                {pct === null
                                  ? "Uploading…"
                                  : pct >= 100
                                    ? "Uploaded, processing on the server…"
                                    : `Uploading… ${pct}% (${formatFileSize(p.loaded)} of ${formatFileSize(file.size)})`}
                              </div>
                            </div>
                          );
                        })}
                        {files.failed.map(({ file, message }, idx) => {
                          const truncatedFilename = truncate(
                            file.name,
                            FILENAME_TRUNCATE_START,
                            FILENAME_TRUNCATE_END,
                            "...",
                          );
                          return (
                            <div
                              key={`failed-${idx}-${file.name}`}
                              className={importClass.elem("file-card").mod({ failed: true }).toClassName()}
                              role="alert"
                            >
                              <div className={importClass.elem("file-card-head").toClassName()}>
                                <Tooltip title={file.name}>
                                  <span className={importClass.elem("file-card-name").toClassName()}>
                                    {truncatedFilename}
                                  </span>
                                </Tooltip>
                                <span className={importClass.elem("file-status").mod({ failed: true }).toClassName()} />
                                <Button
                                  size="small"
                                  look="string"
                                  onClick={() => dispatch({ dismissFailed: file })}
                                  aria-label={`Dismiss ${file.name}`}
                                >
                                  Dismiss
                                </Button>
                              </div>
                              <div className={importClass.elem("file-card-meta").mod({ failed: true }).toClassName()}>
                                Not uploaded: {message}. The other files are unaffected; drop this one again to retry.
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </SimpleCard>
                  </div>
                )}

                {ff.isFF(ff.FF_JSON_PREVIEW) && (
                  <div className="w-full h-full flex flex-col min-h-[400px]">
                    {projectConfigured ? (
                      <SimpleCard
                        title={t("misc.expected_input_preview")}
                        className="w-full h-full overflow-hidden flex flex-col"
                        contentClassName="h-[calc(100%-48px)]"
                        flushContent
                      >
                        {sampleConfig.data ? (
                          <div className={importClass.elem("code-wrapper").toClassName()}>
                            <CodeBlock
                              title={t("misc.expected_input_preview")}
                              code={sampleConfig?.data ?? ""}
                              className="w-full h-full"
                            />
                          </div>
                        ) : sampleConfig.isLoading ? (
                          <div className="w-full flex justify-center py-12">
                            <Spinner className="h-6 w-6" />
                          </div>
                        ) : sampleConfig.isError ? (
                          <div className="w-[calc(100%-24px)] text-lg text-negative-content bg-negative-background border m-3 rounded-md border-negative-border-subtle p-4">
                            Something went wrong, the sample data could not be loaded.
                          </div>
                        ) : null}
                      </SimpleCard>
                    ) : (
                      <SimpleCard className="w-full h-full flex flex-col items-center justify-center text-center p-wide">
                        <div className="flex flex-col items-center gap-tight">
                          <div className="bg-primary-background rounded-largest p-tight flex items-center justify-center">
                            <IconCode className="w-6 h-6 text-primary-icon" />
                          </div>
                          <div className="flex flex-col items-center gap-tighter">
                            <div className="text-label-small text-neutral-content font-medium">
                              {t("misc.view_json_input_format")}
                            </div>
                            <div className="text-body-small text-neutral-content-subtler text-center">
                              Setup your{" "}
                              <Button
                                type="button"
                                look="string"
                                onClick={openConfig}
                                className="border-none bg-none p-0 m-0 text-primary-content underline"
                              >
                                labeling configuration
                              </Button>{" "}
                              first to preview the expected JSON data format
                            </div>
                          </div>
                        </div>
                      </SimpleCard>
                    )}
                  </div>
                )}
              </div>
            </Upload>
          </main>
        ) : dataSourceTab === "paste" ? (
          <main>
            <div className={importClass.elem("paste-wrap").toClassName()}>
              <form className={importClass.elem("paste-form").toClassName()} onSubmit={onImportPasted}>
                <textarea
                  ref={pasteRef}
                  className={importClass.elem("paste-input").toClassName()}
                  placeholder={
                    '[\n  { "data": { "prompt": "First task" } },\n  { "data": { "prompt": "Second task" } }\n]'
                  }
                  rows={12}
                  spellCheck={false}
                  aria-label="Tasks JSON"
                  data-testid="import-paste-input"
                />
                <div className={importClass.elem("paste-actions").toClassName()}>
                  <Button variant="primary" look="outlined" type="submit">
                    {t("pages.createproject.import.paste_submit", undefined, "Import pasted tasks")}
                  </Button>
                  <span className={importClass.elem("paste-hint").toClassName()}>
                    {t(
                      "pages.createproject.import.paste_hint",
                      undefined,
                      "JSON array of tasks, a single task object, or JSONL (one object per line).",
                    )}
                  </span>
                </div>
              </form>
              {showList && (
                <div className="w-full">
                  <SimpleCard
                    title={t("misc.files")}
                    className="w-full h-full"
                    contentClassName="overflow-y-auto h-[calc(100%-48px)]"
                  >
                    <div className={importClass.elem("file-cards").toClassName()}>
                      {files.uploaded.map((file) => (
                        <div key={file.id} className={importClass.elem("file-card").toClassName()}>
                          <div className={importClass.elem("file-card-head").toClassName()}>
                            <Tooltip title={file.file}>
                              <span className={importClass.elem("file-card-name").toClassName()}>
                                {truncate(file.file, FILENAME_TRUNCATE_START, FILENAME_TRUNCATE_END, "…")}
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                      ))}
                    </div>
                  </SimpleCard>
                </div>
              )}
            </div>
          </main>
        ) : (
          <main>
            <div
              className={scn("flex gap-4 w-full min-h-full", {
                "justify-center": !showList,
              })}
            >
              {showList ? (
                <div className="w-full">
                  <SimpleCard
                    title={t("misc.urls", undefined, "URLs")}
                    className="w-full h-full"
                    contentClassName="overflow-y-auto h-[calc(100%-48px)]"
                  >
                    <div className={importClass.elem("file-cards").toClassName()}>
                      {files.uploaded.map((file) => (
                        <div key={file.id} className={importClass.elem("file-card").toClassName()}>
                          <div className={importClass.elem("file-card-head").toClassName()}>
                            <Tooltip title={file.file}>
                              <span className={importClass.elem("file-card-name").toClassName()}>
                                {truncate(file.file, FILENAME_TRUNCATE_START, FILENAME_TRUNCATE_END, "…")}
                              </span>
                            </Tooltip>
                            <Button
                              type="button"
                              look="string"
                              aria-label="Remove URL"
                              onClick={() => dispatch({ removeId: file.id })}
                              className={importClass.elem("file-card-remove").toClassName()}
                            >
                              <IconTrash />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </SimpleCard>
                </div>
              ) : (
                <div className={importClass.elem("urls-empty").toClassName()}>
                  <p>
                    Paste a dataset URL above and press <strong>Add URL</strong> to add it to the import list.
                  </p>
                  <p className="mt-2 text-neutral-content-subtler">
                    Multiple URLs can be added one at a time. Each becomes its own task.
                  </p>
                </div>
              )}
            </div>
          </main>
        ))}

      {/* Per-file column-mapping modal — one file at a time. "Keep as is"
          just dismisses; Apply records the mapping for the final commit. */}
      {splitting && project?.id && (
        <MediaSplitter
          projectId={project.id}
          source={{ fileUploadId: splitting.id }}
          mode="import"
          onClose={() => setSplitting(null)}
          onDone={async ({ job } = {}) => {
            // The clips replace the original in the upload list: the
            // original upload is deleted on the server by the splitter,
            // the clip uploads join the list and get imported (and
            // validated, mapped, previewed) with everything else.
            dispatch({ removeId: splitting.id });
            const clipIds = (job?.results ?? []).map((r) => r.file_upload_id).filter((id) => id != null);
            if (clipIds.length) {
              await loadFilesList(clipIds);
              toast?.show({
                message: `${clipIds.length} ${clipIds.length === 1 ? "clip" : "clips"} replaced ${splitting.file?.split("/").pop() ?? "the recording"} in the upload list.`,
              });
            }
          }}
        />
      )}
      {mappingQueue.length > 0 && (
        <CsvMappingModal
          key={mappingQueue[0].fileId}
          fileName={(files.uploaded.find((f) => f.id === mappingQueue[0].fileId)?.file || "uploaded file")
            .split("/")
            .pop()}
          columns={mappingQueue[0].columns}
          requiredKeys={requiredKeysList}
          initial={csvMappings?.[mappingQueue[0].fileId]}
          onApply={(mapping) => {
            setCsvMappings?.((prev) => ({ ...prev, [mappingQueue[0].fileId]: mapping }));
            setMappingQueue((q) => q.slice(1));
          }}
          onCancel={() => setMappingQueue((q) => q.slice(1))}
        />
      )}
    </div>
  );
};
